import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, HelpCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import SpaceTypeStep from '../components/questionnaire/SpaceTypeStep';
import FacingDirectionStep from '../components/questionnaire/FacingDirectionStep';
import EntrancePositionStep from '../components/questionnaire/EntrancePositionStep';
import KitchenLocationStep from '../components/questionnaire/KitchenLocationStep';
import BedroomLocationStep from '../components/questionnaire/BedroomLocationStep';
import BathroomLocationStep from '../components/questionnaire/BathroomLocationStep';
import PujaAreaStep from '../components/questionnaire/PujaAreaStep';
import WaterStaircaseStep from '../components/questionnaire/WaterStaircaseStep';
import ExtraContextStep from '../components/questionnaire/ExtraContextStep';
import FloorPlanPreview from '../components/questionnaire/FloorPlanPreview';
import axios from 'axios';
import { API } from '../config';

const TOTAL_STEPS = 9;

const Questionnaire = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [answers, setAnswers] = useState({
    spaceType: null,
    facingDirection: null,
    entrancePosition: null,
    kitchenLocation: null,
    bedroomLocation: null,
    bathroomLocations: [],
    pujaArea: null,
    pujaLocation: null,
    waterElements: {
      undergroundTank: false,
      overheadTank: false,
      staircase: false,
      tankLocations: {}
    },
    extraContext: {
      floor: null,
      shape: null,
      cutsExtensions: null,
      cutLocations: []
    }
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateAnswer = (key, value) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
  };

  const handleNext = () => {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate('/');
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      // Convert questionnaire answers to analysis format
      const analysisData = {
        type: 'questionnaire',
        property_type: answers.spaceType || 'home',
        orientation: answers.facingDirection || 'unknown',
        questionnaire_data: answers,
        created_via: 'no-floor-plan'
      };

      // Submit to backend for analysis
      const response = await axios.post(
        `${API}/vastu/analyze-questionnaire`,
        analysisData,
        { withCredentials: true }
      );

      // Navigate to report with analysis ID
      navigate(`/report/${response.data.id}`, {
        state: { fromQuestionnaire: true }
      });
    } catch (error) {
      console.error('Questionnaire submission error:', error);
      alert('Failed to analyze. Please try again.');
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <SpaceTypeStep value={answers.spaceType} onChange={(val) => updateAnswer('spaceType', val)} />;
      case 2:
        return <FacingDirectionStep value={answers.facingDirection} onChange={(val) => updateAnswer('facingDirection', val)} />;
      case 3:
        return <EntrancePositionStep value={answers.entrancePosition} onChange={(val) => updateAnswer('entrancePosition', val)} />;
      case 4:
        return <KitchenLocationStep value={answers.kitchenLocation} onChange={(val) => updateAnswer('kitchenLocation', val)} />;
      case 5:
        return <BedroomLocationStep value={answers.bedroomLocation} onChange={(val) => updateAnswer('bedroomLocation', val)} />;
      case 6:
        return <BathroomLocationStep value={answers.bathroomLocations} onChange={(val) => updateAnswer('bathroomLocations', val)} />;
      case 7:
        return <PujaAreaStep 
          pujaArea={answers.pujaArea} 
          pujaLocation={answers.pujaLocation}
          onPujaChange={(val) => updateAnswer('pujaArea', val)}
          onLocationChange={(val) => updateAnswer('pujaLocation', val)}
        />;
      case 8:
        return <WaterStaircaseStep value={answers.waterElements} onChange={(val) => updateAnswer('waterElements', val)} />;
      case 9:
        return <ExtraContextStep value={answers.extraContext} onChange={(val) => updateAnswer('extraContext', val)} />;
      default:
        return null;
    }
  };

  const progress = (currentStep / TOTAL_STEPS) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[var(--accent-saffron)] via-white to-[var(--accent-blue-200)] py-8 md:py-12 px-4">
      {/* Desktop: Side-by-side layout | Mobile: Stacked layout */}
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Main Content Column */}
          <div className="flex-1 lg:max-w-3xl">
            {/* Progress Header */}
            <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-[#232323]">Quick Vastu Check</h2>
                  <p className="text-sm text-[#353535] mt-1">Step {currentStep} of {TOTAL_STEPS}</p>
                </div>
                <div className="bg-[var(--accent-blue-200)] p-2 md:p-3 rounded-full">
                  <HelpCircle className="w-5 h-5 md:w-6 md:h-6 text-[#232323]" />
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-2 md:h-3">
                <div
                  className="bg-[#D97706] h-2 md:h-3 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Mobile Preview - Above Question */}
            <div className="lg:hidden mb-6">
              <FloorPlanPreview answers={answers} compact={true} />
            </div>

            {/* Question Card */}
            <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
              {renderStep()}
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between mt-6">
              <button
                onClick={handleBack}
                className="flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-white text-[#232323] border-2 border-[#232323] rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm md:text-base"
              >
                <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
                Back
              </button>
              
              <button
                onClick={handleNext}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-6 md:px-8 py-2 md:py-3 bg-[#232323] text-white rounded-lg hover:bg-[#353535] transition-colors font-semibold text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {currentStep === TOTAL_STEPS ? (
                  isSubmitting ? 'Analyzing...' : 'Get My Report'
                ) : (
                  <>
                    Next
                    <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
                  </>
                )}
              </button>
            </div>

            {/* Help Text */}
            <p className="text-center text-xs md:text-sm text-[#353535] mt-4">
              💡 Don't worry if you're not sure about something – every answer helps!
            </p>
          </div>

          {/* Desktop Preview Column - Right Side */}
          <div className="hidden lg:block lg:w-72 xl:w-80">
            <div className="sticky top-24">
              <FloorPlanPreview answers={answers} compact={false} />
              
              {/* Vastu Tips based on current step */}
              <div className="mt-4 bg-[#FAF6EE] border border-[#EBE6DD] rounded-xl p-4">
                <h4 className="font-bold text-[#232323] text-sm mb-2">💡 Vastu Tip</h4>
                <p className="text-xs text-[#353535]">
                  {currentStep === 1 && "Different property types have different Vastu considerations. A flat may have limited options compared to an independent house."}
                  {currentStep === 2 && "North and East-facing entrances are generally considered most auspicious in Vastu."}
                  {currentStep === 3 && "The main entrance should ideally be in the North, East, or Northeast direction."}
                  {currentStep === 4 && "The kitchen is best placed in the Southeast corner, representing the fire element (Agni)."}
                  {currentStep === 5 && "Master bedroom in the Southwest promotes stability and good relationships."}
                  {currentStep === 6 && "Bathrooms are best placed in the North-Northwest or South-Southeast zones."}
                  {currentStep === 7 && "The prayer room thrives in the Northeast corner, the most sacred zone in Vastu."}
                  {currentStep === 8 && "Water elements in the Northeast bring prosperity. Staircases work well in the South or West."}
                  {currentStep === 9 && "The overall shape of your home affects energy flow. Square or rectangular shapes are ideal."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Questionnaire;
